import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import VideoPlayer from '@/pages/Courses/ClassDetail/Partials/VideoPlayer';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { Link } from '@inertiajs/react';
import { ChevronLeft, Clock, Download, FileText, User } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LEVELS } from './liveSessionsConfig';
import {
    extractDateFromTitle,
    LanguageLiveSession,
} from './Partials/LiveSessionCard';

interface Props {
    session: LanguageLiveSession & { description?: string };
}

const LanguageLiveSessionDetailContent: React.FC<Props> = ({ session }) => {
    const { t } = useTranslation();
    const { route } = useRoutes();

    const level = LEVELS[session.level] ?? LEVELS.general;
    const date = extractDateFromTitle(session.title);

    return (
        <AppLayout>
            <PageTitle title={session.title} />
            <div className="mx-auto max-w-4xl space-y-6">
                {/* Back link */}
                <Link
                    href={route('language_live_sessions').toString()}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-neutral-400 dark:hover:text-white"
                >
                    <ChevronLeft className="h-4 w-4" />
                    {t('languageLiveSessions.back')}
                </Link>

                {/* Header */}
                <GlassCard className="overflow-hidden shadow-lg">
                    <div
                        className="h-1 w-full"
                        style={{ backgroundColor: level.color }}
                    />
                    <div className="flex flex-col gap-3 p-6">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span
                                className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
                                style={{ backgroundColor: level.color }}
                            >
                                {t(level.labelKey)}
                            </span>
                            {date ? (
                                <span className="text-xs text-gray-500 dark:text-neutral-400">
                                    {date.toLocaleDateString('es-ES', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                    })}
                                </span>
                            ) : null}
                        </div>

                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                            {session.title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-4">
                            {session.professor?.name ? (
                                <div className="flex items-center gap-2">
                                    {session.professor.avatar_url ? (
                                        <img
                                            src={session.professor.avatar_url}
                                            alt={session.professor.name}
                                            className="h-8 w-8 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 dark:bg-neutral-700">
                                            <User className="h-4 w-4 text-gray-500 dark:text-neutral-400" />
                                        </div>
                                    )}
                                    <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                                        {session.professor.name}
                                    </span>
                                </div>
                            ) : null}
                            {session.duration_minutes > 0 ? (
                                <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-neutral-400">
                                    <Clock className="h-4 w-4 shrink-0" />
                                    {t('languageLiveSessions.duration', {
                                        minutes: session.duration_minutes,
                                    })}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </GlassCard>

                {/* Video player */}
                <GlassCard className="overflow-hidden shadow-lg">
                    <VideoPlayer
                        videoUrl={session.video_url}
                        vimeoUrl={session.vimeo_url}
                    />
                </GlassCard>

                {/* Description */}
                {session.description ? (
                    <GlassCard className="p-6 shadow-lg">
                        <div
                            className="html-content leading-relaxed text-gray-700 dark:text-neutral-300"
                            dangerouslySetInnerHTML={{
                                __html: cleanWordPressHtml(session.description),
                            }}
                        />
                    </GlassCard>
                ) : null}

                {/* Materials */}
                {session.materials.length > 0 ? (
                    <GlassCard className="p-6 shadow-lg">
                        <h2 className="mb-3 text-sm font-semibold text-gray-700 dark:text-neutral-300">
                            {t('languageLiveSessions.materials')}
                        </h2>
                        <ul className="space-y-2">
                            {session.materials.map((material) => (
                                <li key={material.id}>
                                    <a
                                        href={material.file_url ?? '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-lg border border-white/50 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-white/20 dark:border-white/10 dark:text-neutral-300"
                                    >
                                        <FileText className="h-4 w-4 shrink-0 text-gray-400 dark:text-neutral-500" />
                                        <span className="flex-1 truncate">
                                            {material.title}
                                        </span>
                                        <Download className="h-4 w-4 shrink-0 text-gray-400 dark:text-neutral-500" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </GlassCard>
                ) : null}
            </div>
        </AppLayout>
    );
};

const LanguageLiveSessionDetail: React.FC<Props> = ({ session }) => (
    <ThemeWrapper>
        <LanguageLiveSessionDetailContent session={session} />
    </ThemeWrapper>
);

export default LanguageLiveSessionDetail;
